import { Fragment } from "react";
import { parseApiDate } from "@marekl/adminer/datetime";
import { formatTime, formatWeekday } from "@marekl/adminer/format";
import type {
  StartListDay,
  StartListDocument,
  StartListEntry,
  StartListItem,
} from "../../lib/queries/startlists";
import { BLOCK_KIND_LABELS, PREVIEW_NOTICE } from "../../lib/startListState";
import type { ID } from "../../lib/id";
import type { ExportOptions } from "../../lib/startListExport";
import { TeamFlag } from "../TeamFlag";
import styles from "./StartListSheet.module.css";

interface StartListSheetProps {
  doc: StartListDocument;
  options: ExportOptions;
  /** Rows of this team are marked on screen (never in print), so a leader
   * finds their own starts in a list of ninety. */
  highlightTeam?: ID;
}

/**
 * The published running order laid out the way the federation printed it
 * before this system (the 2026 Excel → PDF lists, e.g. Finále MČR
 * Teplice): per day a title line and the harmonogram, then one grey
 * „Disciplína" heading per category over rows of start number, names,
 * club, ČP and time; blocks inline as „12:15 - 12:45 Obědová pauza".
 * People have read that page for years, so this one reads the same.
 *
 * A real <table>: the columns have to line up down a whole page, which is
 * the one thing the flat-row renderer this replaced could not do. The
 * XLSX and PDF exports (phase 2) render the same layout server-side from
 * the same ExportOptions.
 */
export function StartListSheet({ doc, options, highlightTeam }: StartListSheetProps) {
  // A club's cut leaves out the days it does not start on: each day is a
  // page, and a page saying only „nemá žádné starty" is paper wasted.
  const days = options.team ? doc.days.filter(hasStarts) : doc.days;
  return (
    <div className={styles.sheet}>
      {days.map((day) => (
        <DaySheet
          key={day.id}
          day={day}
          doc={doc}
          options={options}
          multiDay={doc.days.length > 1}
          team={highlightTeam}
        />
      ))}
    </div>
  );
}

function hasStarts(day: StartListDay) {
  return day.sections.some((s) => s.items.some((i) => printedBlocks(i).length > 0));
}

/** The category blocks an item prints as. A skupina drah prints lane by
 * lane, each block as an ordinary category: lane 1's blocks, then lane
 * 2's, numbers as they fall (they alternate between the lanes). */
function printedBlocks(item: StartListItem): StartListItem[] {
  if (item.kind === "discipline") return [item];
  if (item.kind === "lanes") return (item.lanes ?? []).flatMap((l) => l.items);
  return [];
}

/** „12:15–12:45"; a block with no end (a parallel one left open) just
 * its start, rather than „7:00–7:00". */
function span(from: StartListItem["start_at"], to: StartListItem["end_at"]) {
  return to.isAfter(from) ? `${formatTime(from)}–${formatTime(to)}` : formatTime(from);
}

function DaySheet({
  day,
  doc,
  options,
  multiDay,
  team,
}: {
  day: StartListDay;
  doc: StartListDocument;
  options: ExportOptions;
  multiDay: boolean;
  team?: ID;
}) {
  const extra = [options.count, options.cp, options.times].filter(Boolean).length;
  // Start numbers print only once „zveřejněno": before that they follow
  // position and the server sends a club none at all.
  const numbers = doc.published;
  const cols = (numbers ? 3 : 2) + extra;
  const hasRows = hasStarts(day);
  return (
    <section className={styles.day}>
      <h2 className={styles.title}>
        {multiDay && <span className={styles.weekday}>{formatWeekday(day.date)} </span>}
        {/* „9. 5. 2026", the federation's own spelling, not the app's
            zero-padded date. */}
        {parseApiDate(day.date)?.format("D. M. YYYY")} {doc.competition_name} – startovní
        listina
      </h2>
      {/* On every day, since each day is its own printed page. */}
      {doc.stage === "preview" && <p className={styles.draftNote}>{PREVIEW_NOTICE}</p>}

      {options.timetable && day.timetable.length > 0 && (
        <div className={styles.timetable}>
          <h3>Časový harmonogram soutěže</h3>
          <table>
            <tbody>
              {day.timetable.map((row, i) => (
                <tr key={i}>
                  <td className={styles.ttLabel}>{row.label}</td>
                  <td className={styles.ttTime}>
                    {span(row.start_at, row.end_at)}
                    {row.expected_start_at && (
                      <span
                        className={styles.expected}
                        aria-label={`předpoklad ${formatTime(row.expected_start_at)}`}
                      >
                        {" "}≈ {formatTime(row.expected_start_at)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className={styles.note}>
            Časy jsou pouze orientační. Časový harmonogram se může v průběhu soutěže měnit.
            {/* Printed too: the paper is what the hall reads. */}
            {doc.missing_time_limits > 0 &&
              " Některé kategorie nemají v pravidlech časový limit, časy za nimi jsou odhad."}
          </p>
        </div>
      )}

      {hasRows ? (
        <table className={styles.grid}>
          <colgroup>
            {numbers && <col className={styles.colNum} />}
            <col />
            <col className={styles.colClub} />
            {options.count && <col className={styles.colSmall} />}
            {options.cp && <col className={styles.colSmall} />}
            {options.times && <col className={styles.colSmall} />}
          </colgroup>
          {day.sections.map((section) => (
            <SectionRows
              key={section.id}
              name={day.sections.length > 1 ? section.name : undefined}
              items={section.items}
              options={options}
              cols={cols}
              numbers={numbers}
              team={team}
            />
          ))}
        </table>
      ) : (
        <p className={styles.empty}>Tento den nemá žádné starty.</p>
      )}
    </section>
  );
}

function SectionRows({
  name,
  items,
  options,
  cols,
  numbers,
  team,
}: {
  name?: string;
  items: StartListItem[];
  options: ExportOptions;
  cols: number;
  /** The day prints the start-number column (only once published). */
  numbers: boolean;
  team?: ID;
}) {
  const optionalHeads = (
    <>
      {options.count && (
        <th scope="col" className={styles.num}>
          Počet
        </th>
      )}
      {options.cp && (
        <th scope="col" className={styles.num}>
          ČP
        </th>
      )}
      {options.times && (
        <th scope="col" className={styles.num}>
          Čas
        </th>
      )}
    </>
  );
  return (
    <>
      {/* One part is the default shape, so its heading would be noise; a
          split day names each, as the 2024 lists did. */}
      {name && (
        <tbody>
          <tr className={styles.part}>
            <td colSpan={cols}>{name}</td>
          </tr>
        </tbody>
      )}
      {items.map((item) =>
        item.kind === "lanes" ? (
          <Fragment key={item.id}>
            {printedBlocks(item).map((b) => (
              <DisciplineBody
                key={b.id}
                item={b}
                options={options}
                numbers={numbers}
                team={team}
                optionalHeads={optionalHeads}
              />
            ))}
          </Fragment>
        ) : item.kind === "block" ? (
          // A parallel block (prezence 7:00 - 15:00) runs alongside the
          // starts rather than between two of them, so inline it would
          // sit at a point in the order it does not belong to. The
          // harmonogram lists it; without one, it stays inline.
          item.parallel && options.timetable ? null : (
          <tbody key={item.id}>
            <tr className={styles.block}>
              {numbers && <td />}
              <td colSpan={numbers ? cols - 1 : cols}>
                {span(item.start_at, item.end_at)}{" "}
                {item.label || (item.block_kind ? BLOCK_KIND_LABELS[item.block_kind] : "Blok")}
              </td>
            </tr>
          </tbody>
          )
        ) : (
          <DisciplineBody
            key={item.id}
            item={item}
            options={options}
            numbers={numbers}
            team={team}
            optionalHeads={optionalHeads}
          />
        ),
      )}
    </>
  );
}

function DisciplineBody({
  item,
  options,
  numbers,
  team,
  optionalHeads,
}: {
  item: StartListItem;
  options: ExportOptions;
  numbers: boolean;
  team?: ID;
  optionalHeads: React.ReactNode;
}) {
  return (
    <tbody className={styles.category}>
      <tr className={styles.catHead}>
        {/* „Disciplína" is the paper's visual label, not a header; the
            category name heads its rowgroup. */}
        {numbers && <td aria-hidden="true">Disciplína</td>}
        <th scope="rowgroup" colSpan={2}>
          {item.competition_category_name}
        </th>
        {optionalHeads}
      </tr>
      {(item.entries ?? []).map((entry) => (
        <EntryTr
          key={entry.id}
          entry={entry}
          options={options}
          team={team}
          numbers={numbers}
        />
      ))}
    </tbody>
  );
}

/** One start on the sheet. */
function EntryTr({
  entry,
  options,
  team,
  numbers,
}: {
  entry: StartListEntry;
  options: ExportOptions;
  team?: ID;
  numbers: boolean;
}) {
  const mine = !!team && entry.team_id === team;
  const className = [entry.scratched ? styles.scratched : "", mine ? styles.mine : ""]
    .filter(Boolean)
    .join(" ");
  // A formation's title IS the club, and the legacy lists left the club
  // cell empty rather than print the name twice.
  const club = entry.title === entry.team_name ? "" : entry.team_name;
  return (
    <tr className={className || undefined}>
      {numbers && (
        <td className={styles.num} aria-label={`Startovní číslo ${entry.display_number}`}>
          {entry.display_number}
        </td>
      )}
      <td>
        {entry.title}
        {!club && entry.team_country && (
          <>
            {" "}
            <TeamFlag code={entry.team_country} withCode={false} />
          </>
        )}
        {entry.scratched && <span className={styles.srOnly}> (přeškrtnuto)</span>}
        {mine && <span className={styles.srOnly}> (vybraný tým)</span>}
      </td>
      <td>
        {club}
        {club && entry.team_country && (
          <>
            {" "}
            <TeamFlag code={entry.team_country} withCode={false} />
          </>
        )}
      </td>
      {options.count && <td className={styles.num}>{entry.competitors?.length ?? ""}</td>}
      {options.cp && <td className={styles.num}>{entry.entry_number}</td>}
      {options.times && (
        <td className={styles.num}>
          {formatTime(entry.start_at)}
          {/* On screen only: the printed sheet is the published one. */}
          {entry.expected_start_at && (
            <span
              className={styles.expected}
              aria-label={`předpoklad ${formatTime(entry.expected_start_at)}`}
            >
              {" "}≈ {formatTime(entry.expected_start_at)}
            </span>
          )}
        </td>
      )}
    </tr>
  );
}
